import type { ApiError, ApiIssue } from '@ralph/contracts'

/**
 * Thin fetch wrapper.
 *
 * Two responsibilities beyond `fetch`: it carries the CSRF token that the server's
 * double-submit check requires on every mutating request, and it turns the canonical error
 * envelope into a typed exception so the UI can branch on `code` rather than parse prose.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly issues: ApiIssue[],
    readonly requestId: string
  ) {
    super(issues[0]?.message ?? `request failed with status ${status}`)
    this.name = 'ApiRequestError'
  }

  get code(): string {
    return this.issues[0]?.code ?? 'unknown'
  }

  get retryable(): boolean {
    return this.issues.some((i) => i.retryable)
  }
}

let csrfToken: string | null = null

export const setCsrfToken = (token: string | null): void => {
  csrfToken = token
}

export const getCsrfToken = (): string | null => csrfToken

export const buildHeaders = (extra?: HeadersInit): Headers => {
  const headers = new Headers(extra)
  if (csrfToken) headers.set('x-ralph-csrf', csrfToken)
  return headers
}

const parseError = async (response: Response): Promise<ApiRequestError> => {
  try {
    const body = (await response.json()) as ApiError
    if (Array.isArray(body?.issues) && body.issues.length > 0) {
      return new ApiRequestError(response.status, body.issues, body.requestId ?? '')
    }
  } catch {
    /* fall through to the generic shape below */
  }

  return new ApiRequestError(
    response.status,
    [
      {
        code: 'unexpected_response',
        message: response.statusText || 'The request failed.',
        field: null,
        retryable: response.status >= 500
      }
    ],
    ''
  )
}

export const api = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = buildHeaders(init.headers)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')

  const response = await fetch(path, { ...init, headers, credentials: 'same-origin' })
  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const apiGet = <T>(path: string): Promise<T> => api<T>(path)

export const apiSend = <T>(
  path: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  body?: unknown,
  headers?: HeadersInit
): Promise<T> =>
  api<T>(path, {
    method,
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(headers === undefined ? {} : { headers })
  })
